import { useEffect, useRef, useState } from 'react';
import { YoutubeIframeVideoController } from '../adapters/video/YoutubeIframeVideoController.ts';
import { updateRecentVideoTitle } from '../lib/recentVideos.ts';
import type { YtNamespace } from '../lib/youtubeApi.ts';

export type UseYoutubePlayerOptions = {
  /** Inject YT namespace (tests / offline). */
  yt?: YtNamespace;
  host?: Window & typeof globalThis;
};

export type UseYoutubePlayerResult = {
  containerRef: React.RefObject<HTMLDivElement>;
  ready: boolean;
  error: string | null;
  controller: YoutubeIframeVideoController | null;
};

export function useYoutubePlayer(
  videoId: string | null,
  options: UseYoutubePlayerOptions = {},
): UseYoutubePlayerResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [controller, setController] = useState<YoutubeIframeVideoController | null>(null);

  useEffect(() => {
    if (!videoId) {
      setReady(false);
      setError(null);
      setController(null);
      return;
    }

    let cancelled = false;
    let ctrl: YoutubeIframeVideoController | null = null;
    let raf = 0;

    const start = (el: HTMLElement) => {
      ctrl = new YoutubeIframeVideoController({
        container: el,
        videoId,
        yt: options.yt,
        host: options.host,
      });
      setReady(false);
      setError(null);
      setController(ctrl);

      void ctrl
        .init()
        .then(() => {
          if (cancelled) return;
          setReady(true);
          const rememberTitle = () => {
            const title = ctrl?.getVideoTitle();
            if (title) updateRecentVideoTitle(videoId, title);
            return Boolean(title);
          };
          // Title is usually ready on onReady; retry once if YT returns empty briefly.
          if (!rememberTitle()) {
            window.setTimeout(() => {
              if (!cancelled) rememberTitle();
            }, 500);
          }
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : 'Failed to load player');
          setReady(false);
        });
    };

    // Wait one frame if the ref is not attached yet (first paint / Strict Mode).
    const el = containerRef.current;
    if (el) {
      start(el);
    } else {
      raf = requestAnimationFrame(() => {
        const next = containerRef.current;
        if (!next || cancelled) return;
        start(next);
      });
    }

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      ctrl?.dispose();
      setController(null);
      setReady(false);
    };
  }, [videoId, options.yt, options.host]);

  return { containerRef, ready, error, controller };
}
